import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import {DocumentNode} from 'graphql';
import React from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {WithoutTypenames} from 'graphql-client/WithoutTypenames';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Size} from 'ui-library/inputs/InputConstants';
import TextInput from 'ui-library/inputs/TextInput';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth, OptionContentType, OptionData} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';
import Section from 'ui-library/menus/Section';
import PreventParentClickWrapper from 'ui-library/wrappers/PreventParentClickWrapper';
import {InputChangeEvent} from 'util/Types';

import {
  InstitutionSearchInputQuery,
  InstitutionSearchInputQueryVariables,
} from './__generated/InstitutionSearchInputQuery';

export type InstitutionSearchInputInstitution = WithoutTypenames<
  InstitutionSearchInputQuery['currentTenantInstitutionListing']['institutions'][number]
>;

export const INSTITUTION_SEARCH_INPUT_QUERY: DocumentNode = gql`
  query InstitutionSearchInputQuery(
    $searchInput: InstitutionSearchInputInput!
    $limitOffsetInput: LimitOffsetInput!
    $allowUnverified: StrictBoolean!
  ) @skipAudit @engComponent(name: PARTIES) {
    currentTenantInstitutionListing(
      searchInput: $searchInput
      limitOffsetInput: $limitOffsetInput
      allowUnverified: $allowUnverified
    ) {
      institutions {
        id
        fullName
        licensed
      }
    }
  }
`;

const LIMIT_OFFSET = {limit: 15, offset: 0};

interface InstitutionSearchInputProps {
  onSelect: (institution: InstitutionSearchInputInstitution) => void;
  menuWidth?: MenuWidth;
  placeholder?: string;
}

export default function InstitutionSearchInput(props: InstitutionSearchInputProps): JSX.Element {
  const [query, setQuery] = React.useState('');
  const queryVariables = {
    searchInput: {query},
    limitOffsetInput: LIMIT_OFFSET,
    allowUnverified: true,
  };

  return (
    <BenchlingQuery<InstitutionSearchInputQuery, InstitutionSearchInputQueryVariables>
      query={INSTITUTION_SEARCH_INPUT_QUERY}
      variables={queryVariables}
      renderLoading={() => (
        <PresentationalInstitutionSearchInput
          query={query}
          onChangeQuery={setQuery}
          institutions={[]}
          onSelect={props.onSelect}
          isLoading={true}
          menuWidth={props.menuWidth ?? Menu.WIDTHS.FULL}
          placeholder={props.placeholder}
        />
      )}
      fetchPolicy="cache-and-network"
    >
      {({currentTenantInstitutionListing}) => (
        <PresentationalInstitutionSearchInput
          query={query}
          onChangeQuery={setQuery}
          institutions={currentTenantInstitutionListing.institutions}
          onSelect={props.onSelect}
          isLoading={false}
          menuWidth={props.menuWidth ?? Menu.WIDTHS.FULL}
          placeholder={props.placeholder}
        />
      )}
    </BenchlingQuery>
  );
}

export type PresentationalInstitutionSearchInputProps = {
  institutions: Array<InstitutionSearchInputInstitution>;
  isLoading?: boolean;
  menuWidth?: MenuWidth;
  onChangeQuery: (query: string) => void;
  placeholder?: string;
  query: string;
} & (
  | {
      makeOptionData: (optionValue: InstitutionSearchInputInstitution) => OptionData;
      renderOptionContainer: (optionData: OptionData) => JSX.Element;
      onSelect?: never;
    }
  | {
      makeOptionData?: never;
      renderOptionContainer?: never;
      onSelect: (institution: InstitutionSearchInputInstitution) => void;
    }
);

function renderOptionContainer(
  optionData: OptionData,
  onSelect: (institution: InstitutionSearchInputInstitution) => void
): JSX.Element {
  const handleSelect = (optionValue: string): void => {
    onSelect({
      id: optionValue,
      fullName: optionData.name as string,
      licensed: optionData.value?.licensed,
    });
  };
  return (
    <OptionContainer key={optionData.value} value={optionData.value} onSelect={handleSelect}>
      <span className={css(styles.options)}>{optionData.name}</span>
    </OptionContainer>
  );
}

function renderOptions(
  optionDatas: Array<OptionData>,
  hasSearched: boolean,
  isLoading: boolean,
  onSelect?: (institution: InstitutionSearchInputInstitution) => void,
  renderOptionContainerOverride?: (optionData: OptionData) => JSX.Element
): Array<JSX.Element> | null {
  if (optionDatas.length > 0) {
    return optionDatas.map((optionData) => {
      if (renderOptionContainerOverride != null) {
        return renderOptionContainerOverride(optionData);
      }
      return renderOptionContainer(optionData, onSelect!);
    });
  }
  if (hasSearched) {
    const optionContent = isLoading ? (
      <CenteredHorizontalThrobber />
    ) : (
      <span className={classNames(css(styles.options), 'u-faded')}>No results found.</span>
    );
    const optionKey = isLoading ? 'loading' : 'no-results';
    return [<OptionContainer key={optionKey}>{optionContent}</OptionContainer>];
  }
  return null;
}

export function PresentationalInstitutionSearchInput(
  props: PresentationalInstitutionSearchInputProps
): JSX.Element {
  const [isInputFocused, setIsInputFocused] = React.useState(false);

  const optionDatas =
    props.makeOptionData != null
      ? props.institutions.map((institution) => props.makeOptionData(institution))
      : props.institutions.map((institution: InstitutionSearchInputInstitution) => ({
          name: institution.fullName,
          value: institution.id,
        }));

  const handleChangeQuery = (e: InputChangeEvent): void => {
    props.onChangeQuery(e.target.value);
  };

  return (
    <Menu width={props.menuWidth ?? Menu.WIDTHS.FULL} height={Menu.HEIGHTS.MEDIUM} dropLeft={true}>
      <Section>
        <Option isDisabled={true} isSelected={true} contentType={OptionContentType.INPUT}>
          <PreventParentClickWrapper>
            <TextInput
              className={css(styles.searchBar)}
              placeholder={props.placeholder ?? 'Search'}
              size={Size.MEDIUM}
              value={props.query}
              onChange={handleChangeQuery}
              onFocus={() => {
                setIsInputFocused(true);
              }}
              onBlur={() => {
                setIsInputFocused(false);
              }}
              autoFocus={true}
            />
          </PreventParentClickWrapper>
        </Option>
      </Section>
      {isInputFocused &&
        renderOptions(
          optionDatas,
          props.query.length !== 0,
          props.isLoading ?? false,
          props.onSelect,
          props.renderOptionContainer
        )}
    </Menu>
  );
}

const styles = StyleSheet.create({
  searchBar: {
    display: 'block',
    marginTop: '10px',
    marginBottom: '10px',
    borderRadius: 5,
  },
  options: {
    margin: '10px',
  },
});
